import React, { useState, useMemo, useEffect } from "react";
import { View, ScrollView, TextInput, Pressable, StyleSheet } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { supabase } from "@/lib/supabase-client";

export type DressCode = {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  meta_data: Record<string, any>;
  created_at: string;
};

export type DressCodePickerProps = {
  onPick: (dressCodeId: string) => void;
  onCancel?: () => void;
};

export function DressCodePicker({ onPick, onCancel }: DressCodePickerProps) {
  const { colors } = useTheme();
  const [search, setSearch] = useState("");
  const [dressCode, setDressCodes] = useState<DressCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch dress codes on mount
  useEffect(() => {
    let cancel = false;

    (async () => {
      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .schema("lists")
          .from("dress_codes")
          .select("id, tenant_id, name, description, meta_data, created_at")
          .order("name", { ascending: true });

        if (cancel) return;

        if (fetchError) {
          console.error("Failed to fetch dress codes:", fetchError);
          setError("Failed to load dress codes");
          return;
        }

        setDressCodes(data ?? []);
      } catch (err) {
        if (!cancel) {
          console.error("Unexpected error fetching dress codes:", err);
          setError("An unexpected error occurred");
        }
      } finally {
        if (!cancel) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancel = true;
    };
  }, []);

  const filteredItems = useMemo(() => {
    if (!search) return dressCode;
    const searchLower = search.toLowerCase();
    return dressCode.filter(
      (item) => item.name.toLowerCase().includes(searchLower) || item.description?.toLowerCase().includes(searchLower),
    );
  }, [dressCode, search]);

  return (
    <View testID="dress-code-picker" style={styles.container}>
      <ThemedText type="subtitle">Select Dress Code</ThemedText>

      <TextInput
        testID="dress-code-picker-search"
        style={[
          styles.searchInput,
          {
            borderColor: colors.border,
            color: colors.text,
            backgroundColor: colors.backgroundSecondary,
          },
        ]}
        placeholder="Search dress codes..."
        placeholderTextColor={colors.placeholder}
        value={search}
        onChangeText={setSearch}
        autoFocus
        editable={!loading}
      />

      <ScrollView style={styles.scrollView}>
        {loading ? (
          <ThemedText style={styles.message}>Loading...</ThemedText>
        ) : error ? (
          <ThemedText style={[styles.message, { color: colors.red.text }]}>{error}</ThemedText>
        ) : filteredItems.length > 0 ? (
          filteredItems.map((item) => (
            <Pressable
              key={item.id}
              testID={`dress-code-option-${item.id}`}
              style={[styles.itemRow, { borderBottomColor: colors.border }]}
              onPress={() => onPick(item.id)}
            >
              <View style={styles.itemContent}>
                <ThemedText type="defaultSemiBold">{item.name}</ThemedText>
                {item.description && (
                  <ThemedText style={[styles.description, { color: colors.textSecondary }]}>
                    {item.description}
                  </ThemedText>
                )}
              </View>
            </Pressable>
          ))
        ) : (
          <ThemedText style={styles.message}>{search ? "No dress codes found" : "No dress codes available"}</ThemedText>
        )}
      </ScrollView>

      {onCancel && (
        <View style={styles.footer}>
          <Button
            testID="dress-code-picker-cancel"
            text="Cancel"
            style={[
              styles.cancelButton,
              {
                borderColor: colors.border,
              },
            ]}
            textStyle={{ color: colors.text }}
            onPress={onCancel}
          />
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 8,
    gap: 8,
  },
  searchInput: {
    height: 36,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 14,
  },
  scrollView: {
    maxHeight: 280,
  },
  message: {
    opacity: 0.7,
    alignSelf: "center",
    marginVertical: 8,
  },
  itemRow: {
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    backgroundColor: "transparent",
  },
  itemContent: {
    gap: 4,
  },
  description: {
    fontSize: 12,
  },
  footer: {
    flexDirection: "row",
    gap: 8,
    justifyContent: "flex-end",
    marginTop: 8,
  },
  cancelButton: {
    height: 32,
    paddingHorizontal: 16,
    backgroundColor: "transparent",
    borderWidth: 1,
  },
});
